import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Alert, CircularProgress, Typography,
} from '@mui/material';
import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import { BRAND, TEXT } from '../theme/colors';

const ForgotPassword = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await axios.post(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, { email: email.trim() });
      setStatus('success');
      setMessage(res.data?.detail || (t('passwordResetSent') || 'If that email is registered you will receive a reset link.'));
    } catch (err) {
      if (err.response?.status === 503) {
        setStatus('error');
        setMessage(t('emailServiceUnavailable') || 'Email service unavailable. Please try again later.');
      } else {
        setStatus('success');
        setMessage(t('passwordResetSent') || 'If that email is registered you will receive a reset link.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${BRAND.brownDark} 0%, ${BRAND.green} 50%, ${BRAND.brownMed} 100%)`,
    }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', borderRadius: 3, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src={language === 'el' ? '/light_gr.png' : '/light_en.png'}
            alt="TerraMind"
            sx={{ maxWidth: 340, height: 90, objectFit: 'contain', display: 'block', mx: 'auto', mb: 1.5 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT.primary }}>
            {t('forgotPassword') || 'Forgot Password'}
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT.secondary, mt: 0.5 }}>
            {t('forgotPasswordSubtitle') || 'Enter your email and we will send you a reset link.'}
          </Typography>
        </Box>

        {status && (
          <Alert severity={status} sx={{ mb: 2 }}>{message}</Alert>
        )}

        {status !== 'success' && (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('email') || 'Email'}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 3 }}
              autoFocus
              required
              InputProps={{ sx: { fontSize: '0.95rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !email.trim()}
              sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, bgcolor: BRAND.green, '&:hover': { bgcolor: BRAND.greenDark } }}
            >
              {loading ? <CircularProgress size={24} /> : (t('sendResetLink') || 'Send Reset Link')}
            </Button>
          </form>
        )}

        <Typography sx={{ textAlign: 'center', mt: 2, fontSize: '0.9rem' }}>
          <Link to="/login" style={{ color: BRAND.green, fontWeight: 600, display: 'inline-block', padding: '8px 4px' }}>
            {t('backToLogin') || 'Back to Login'}
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
